import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import { trackUserRegistration, trackConsentAcceptance } from '../services/analytics';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    acceptPrivacyPolicy: false,
    cookieConsent: false,
    analyticsConsent: false,
    marketingConsent: false,
  });
  const [error, setError] = useState('');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect to invite-accept if invite token is provided
  useEffect(() => {
    const invite = searchParams.get('invite');
    if (invite) {
      navigate(`/invite-accept?invite=${invite}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!formData.acceptPrivacyPolicy) {
        setError('You must accept the privacy policy');
        return;
      }
      await register(formData);
      
      // Track registration and consents
      trackUserRegistration(formData.email);
      if (formData.cookieConsent) trackConsentAcceptance('cookies');
      if (formData.analyticsConsent) trackConsentAcceptance('analytics');
      if (formData.marketingConsent) trackConsentAcceptance('marketing');
      
      navigate('/dashboard/overview');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Registration failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold mb-6">SATI Register</h1>

        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="reg-email" className="block text-sm font-medium mb-1">Email</label>
            <input
              id="reg-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              placeholder="your@email.com"
              required
            />
          </div>

          <div>
            <label htmlFor="reg-name" className="block text-sm font-medium mb-1">Name</label>
            <input
              id="reg-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              placeholder="Your full name"
              required
            />
          </div>

          <div>
            <label htmlFor="reg-password" className="block text-sm font-medium mb-1">Password</label>
            <input
              id="reg-password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              placeholder="••••••••"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Min 8 chars, with uppercase, lowercase, and numbers
            </p>
          </div>

          <div className="space-y-2 border-t pt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="acceptPrivacyPolicy"
                checked={formData.acceptPrivacyPolicy}
                onChange={handleChange}
                required
              />
              <span className="ml-2 text-sm">
                I accept the{' '}
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
                >
                  Privacy Policy
                </button>
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="cookieConsent"
                checked={formData.cookieConsent}
                onChange={handleChange}
              />
              <span className="ml-2 text-sm">Allow cookies</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="analyticsConsent"
                checked={formData.analyticsConsent}
                onChange={handleChange}
              />
              <span className="ml-2 text-sm">Allow analytics</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="marketingConsent"
                checked={formData.marketingConsent}
                onChange={handleChange}
              />
              <span className="ml-2 text-sm">Allow marketing emails</span>
            </label>
          </div>

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
          >
            Register
          </button>
        </form>

        <p className="mt-4 text-center">
          Already have an account?{' '}
          <a href="/login" className="text-blue-600 hover:underline">
            Login
          </a>
        </p>
      </div>
    </div>
  );
}
